import { useAppTheme } from '@/providers/theme-provider';
import { useResponsiveBreakpoint } from '@/hooks/use-responsive-breakpoint';
import { AlertTriangle, HelpCircle, Info, Trash2 } from 'lucide-react-native';
import React from 'react';
import { Platform, Text, View } from 'react-native';
import { Button } from '../button';
import { Modal } from '../modal';
import type { ConfirmationDialogProps, ConfirmationVariant } from './types';

const VARIANT_COLORS: Record<ConfirmationVariant, { bg: string; icon: string }> = {
  destructive: { bg: 'rgba(224, 85, 85, 0.15)', icon: '#E05555' },
  warning: { bg: 'rgba(229, 168, 48, 0.15)', icon: '#E5A830' },
  info: { bg: 'rgba(91, 141, 239, 0.15)', icon: '#5B8DEF' },
  default: { bg: 'rgba(154, 205, 50, 0.15)', icon: '#9ACD32' },
};

const DEFAULT_ICONS: Record<ConfirmationVariant, React.ComponentType<{ size: number; color: string }>> = {
  destructive: Trash2,
  warning: AlertTriangle,
  info: Info,
  default: HelpCircle,
};

export function ConfirmationDialog({
  visible,
  onClose,
  variant = 'default',
  icon,
  title,
  description,
  children,
  actions,
  cancelLabel = 'Cancel',
  dismissOnBackdrop = true,
  testID,
}: ConfirmationDialogProps) {
  const theme = useAppTheme();
  const breakpoint = useResponsiveBreakpoint();
  const isDesktop = Platform.OS === 'web' && breakpoint !== 'mobile';

  const colors = VARIANT_COLORS[variant];
  const IconComponent = icon ?? DEFAULT_ICONS[variant];

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      animationType="fade"
      backdropPress={dismissOnBackdrop}
      testID={testID}
    >
      <View
        style={{
          backgroundColor: theme.colors.surface,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: theme.colors.border,
          padding: 24,
          width: '100%',
          maxWidth: 448,
        }}
      >
        {/* Icon Badge */}
        <View style={{ alignItems: isDesktop ? 'flex-start' : 'center', marginBottom: 16 }}>
          <View
            style={{
              width: 48,
              height: 48,
              borderRadius: 24,
              backgroundColor: colors.bg,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconComponent size={24} color={colors.icon} />
          </View>
        </View>

        {/* Title */}
        <Text
          style={{
            fontSize: 18,
            fontWeight: '600',
            color: theme.colors.text,
            textAlign: isDesktop ? 'left' : 'center',
            marginBottom: 8,
            fontFamily: 'Franie-Regular',
          }}
        >
          {title}
        </Text>

        {/* Description */}
        {description != null && (
          typeof description === 'string' ? (
            <Text
              style={{
                fontSize: 14,
                color: theme.colors.textMuted,
                textAlign: isDesktop ? 'left' : 'center',
                lineHeight: 20,
                marginBottom: children ? 16 : 0,
              }}
            >
              {description}
            </Text>
          ) : (
            <View style={{ marginBottom: children ? 16 : 0 }}>
              {description}
            </View>
          )
        )}

        {/* Optional content slot */}
        {children}

        {/* Action Buttons */}
        {isDesktop ? (
          <DesktopActions
            actions={actions}
            cancelLabel={cancelLabel}
            onClose={onClose}
          />
        ) : (
          <MobileActions
            actions={actions}
            cancelLabel={cancelLabel}
            onClose={onClose}
          />
        )}
      </View>
    </Modal>
  );
}

interface ActionLayoutProps {
  actions: ConfirmationDialogProps['actions'];
  cancelLabel: string | null | undefined;
  onClose: () => void;
}

function DesktopActions({ actions, cancelLabel, onClose }: ActionLayoutProps) {
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 8,
        marginTop: 24,
      }}
    >
      {cancelLabel != null && (
        <Button variant="ghost" size="default" onPress={onClose}>
          {cancelLabel}
        </Button>
      )}
      {actions.map((action) => (
        <Button
          key={action.label}
          variant={action.variant === 'primary' ? 'primary' : action.variant === 'destructive' ? 'destructive' : 'primary'}
          size="default"
          onPress={action.onPress}
          loading={action.loading}
        >
          {action.label}
        </Button>
      ))}
    </View>
  );
}

function MobileActions({ actions, cancelLabel, onClose }: ActionLayoutProps) {
  return (
    <View style={{ marginTop: 24, gap: 8 }}>
      {actions.map((action) => (
        <Button
          key={action.label}
          variant={action.variant === 'primary' ? 'primary' : action.variant === 'destructive' ? 'destructive' : 'primary'}
          size="default"
          fullWidth
          onPress={action.onPress}
          loading={action.loading}
        >
          {action.label}
        </Button>
      ))}
      {cancelLabel != null && (
        <Button variant="ghost" size="default" fullWidth onPress={onClose}>
          {cancelLabel}
        </Button>
      )}
    </View>
  );
}
